import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { featuredComics, faqData } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  useEffect(() => {
    const found = featuredComics.find((c) => c.id === Number(id));
    setComic(found || null);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <p className="mt-4 text-foreground-500">漫画未找到</p>
          <Link to="/" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedComics = featuredComics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  return (
    <>
      <title>{comic.title} - 韩漫大全免费在线阅读</title>
      <meta name="description" content={`${comic.title}，${comic.author}创作，${comic.category}类韩国漫画。${comic.description}快来韩漫大全免费阅读！`} />
      <meta name="keywords" content={`${comic.title},${comic.category}漫画,韩漫大全,${comic.author},韩国漫画推荐`} />
      <link rel="canonical" href={`https://hanmandq.com/comic/${comic.id}`} />

      <nav aria-label="面包屑导航" className="w-full bg-background-50 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors duration-200">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors duration-200">{comic.category}漫画</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium">{comic.title}</li>
          </ol>
        </div>
      </nav>

      <article className="w-full pt-10 pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto md:mx-0">
              <div className="rounded-xl overflow-hidden aspect-[3/4] bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫大全`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  {comic.category}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-500">作者</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1">{comic.author}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-500">评分</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1 flex items-center justify-center gap-1">
                    <i className="ri-star-fill text-xs text-accent-500"></i>
                    {comic.rating}
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-500">总章节</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1">{comic.chapters}话</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-500">阅读量</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1">
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <Link
                  to={`/comic/${comic.id}`}
                  className="bg-primary-500 text-background-50 px-7 py-2.5 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer inline-flex items-center gap-2"
                >
                  <i className="ri-play-line"></i> 开始阅读
                </Link>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="bg-background-100 text-foreground-700 px-7 py-2.5 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 transition-colors duration-200 cursor-pointer"
                >
                  更多{comic.category}漫画
                </Link>
              </div>

              <div className="mt-6 flex items-center gap-4 flex-wrap">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-3 py-1 rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === 'info'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                作品介绍
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === 'chapters'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表
              </button>
            </div>

            {activeTab === 'info' ? (
              <div className="mt-6 bg-background-100 rounded-xl p-6">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                <p className="text-sm text-foreground-700 leading-relaxed mt-4">
                  《{comic.title}》是由韩国漫画家{comic.author}精心创作的一部优秀{comic.category}类漫画作品。
                  该作品画风精美，剧情紧凑，深受广大韩漫爱好者的喜爱。
                  目前该作品{comic.status === '连载中' ? '正在火热连载中' : '已经完结'}，
                  共包含{comic.chapters}话精彩内容，累计阅读量超过{comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}次，
                  读者评分高达{comic.rating}分。韩漫大全为您提供该漫画的免费在线阅读服务，支持手机和电脑全平台流畅阅读。
                </p>
              </div>
            ) : (
              <div className="mt-6 bg-background-100 rounded-xl p-6">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">章节列表</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => (
                    <div
                      key={i}
                      className="bg-background-50 rounded-lg px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <span className="text-xs text-foreground-400">第{i + 1}话</span>
                      <span className="text-xs text-foreground-300">-</span>
                      <span className="text-xs truncate">
                        {i === 0 ? '序幕' : i === comic.chapters - 1 ? '最终话' : `精彩内容(${i + 1})`}
                      </span>
                    </div>
                  ))}
                </div>
                {comic.chapters > 24 && (
                  <p className="text-center text-xs text-foreground-400 mt-4">
                    仅展示前24话，共{comic.chapters}话
                  </p>
                )}
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ComicStory",
            "name": comic.title,
            "author": { "@type": "Person", "name": comic.author },
            "description": comic.description,
            "url": `https://hanmandq.com/comic/${comic.id}`,
            "genre": comic.category,
            "dateModified": comic.updatedAt,
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": comic.rating,
              "bestRating": "5",
              "ratingCount": Math.floor(comic.views / 100)
            }
          })
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://hanmandq.com/" },
              { "@type": "ListItem", "position": 2, "name": `${comic.category}漫画`, "item": `https://hanmandq.com/category/${comic.categorySlug}` },
              { "@type": "ListItem", "position": 3, "name": comic.title, "item": `https://hanmandq.com/comic/${comic.id}` }
            ]
          })
        }}
      />
    </>
  );
}